import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import Checkbox from 'honeydew/Checkbox';
import useTailwindTheme from 'hooks/useTailwindTheme';

function PlanCard({ bundles, dispatch, flexible, imperfectLegacy, plan, savedPlan, savedPlanSize }) {
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });

    const planImage = plan?.photoUrlMobile ? plan.photoUrlMobile : plan.imgSrc;

    const handlePlanChange = () => {
        if (savedPlan) {
            return dispatch({
                type: 'removePlan',
                payload: plan.id,
            });
        }

        return dispatch({
            type: 'addPlan',
            payload: {
                id: plan.id,
                name: plan.name,
                subPlanIDs: plan?.subPlans?.map((subPlan) => subPlan.id),
            },
        });
    };

    const handleSubPlanChange = (id) => {
        const changedSubPlanId = Number(id);

        // Selecting a subPlan will add the plan, if the plan isn't already added
        if (!savedPlan) {
            return dispatch({
                type: 'addPlan',
                payload: {
                    id: plan.id,
                    subPlanIDs: [changedSubPlanId],
                },
            });
        }

        // Deselecting the last subPlan will remove the plan
        if (savedPlan.subPlanIDs.includes(changedSubPlanId) && savedPlan.subPlanIDs.length === 1) {
            return dispatch({
                type: 'removePlan',
                payload: plan.id,
            });
        }

        // Otherwise, add or remove the subPlan only
        const newSubPlanIDs = savedPlan.subPlanIDs.includes(changedSubPlanId)
            ? savedPlan.subPlanIDs.filter((subPlanID) => subPlanID !== changedSubPlanId)
            : [...savedPlan.subPlanIDs, changedSubPlanId];

        return dispatch({
            type: 'modifyPlan',
            payload: {
                id: plan.id,
                subPlanIDs: newSubPlanIDs,
            },
        });
    };

    return (
        <div className={clsx('relative', { '-ml-20': mobile })}>
            <Checkbox
                id={`plan${plan.id}`}
                positioningClasses={clsx({
                    'absolute top-[15px] left-[25px]': mobile,
                    '!self-start mt-50': !mobile && plan?.subPlans?.length > 0,
                })}
                checked={Object.keys(savedPlan ?? {})?.length > 0}
                onCheckedChange={handlePlanChange}
                name="plan"
                value={plan.id}
                label={
                    <figure
                        className={clsx('my-10 flex cursor-pointer gap-10 md:gap-20', {
                            'items-start': plan?.subPlans?.length > 0,
                            'items-center': plan?.subPlans?.length === 0,
                        })}
                    >
                        <div className="h-[100px] min-w-[100px] overflow-hidden rounded-md">
                            {/* TODO: update images once they are finalized */}
                            <img
                                src={planImage}
                                alt={`Sample of items found in our ${plan.name} aisle`}
                                className="h-[100px] w-[100px] object-cover"
                            />
                        </div>

                        <figcaption>
                            <div>
                                <p>
                                    <span className="text-body-sm font-grotesk-bold">{plan.name}</span>
                                    {!bundles && !flexible && (
                                        <span className="text-body-xs ml-5 text-grey">
                                            Starts at $
                                            {imperfectLegacy
                                                ? plan.minBoxRange
                                                : plan?.planSizes[savedPlanSize]?.minBoxRange}
                                        </span>
                                    )}
                                </p>

                                <p className="text-body-sm">
                                    {plan?.planSizes ? `Approx. ${plan?.planSizes[savedPlanSize]?.numItems} ` : ''}
                                    {plan?.numItems ? `Approx. ${plan.numItems} items like ` : ''}
                                    {plan.description}
                                </p>

                                {plan?.subPlans?.length > 0 && (
                                    <div className="my-5">
                                        {plan?.subPlans.map(({ description, id, name }) => (
                                            <Checkbox
                                                key={`subPlan${id}`}
                                                id={`subPlan${id}`}
                                                checked={savedPlan?.subPlanIDs?.includes(id) ?? false}
                                                onCheckedChange={() => handleSubPlanChange(id)}
                                                name={`subPlan-${id}`}
                                                value={id}
                                                wrapperClassName="mb-5"
                                                label={
                                                    <label key={name} htmlFor={`subPlan${id}`}>
                                                        <div>
                                                            <p className="text-body-sm">{name}</p>
                                                            {description && (
                                                                <p className="text-body-xs text-grey">{description}</p>
                                                            )}
                                                        </div>
                                                    </label>
                                                }
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </figcaption>
                    </figure>
                }
            />
        </div>
    );
}

PlanCard.propTypes = {
    bundles: PropTypes.bool,
    dispatch: PropTypes.func.isRequired,
    flexible: PropTypes.bool,
    imperfectLegacy: PropTypes.bool,
    plan: PropTypes.shape({
        coldPack: PropTypes.bool,
        description: PropTypes.string,
        id: PropTypes.number,
        name: PropTypes.string,
        photoUrlDesktop: PropTypes.string,
        photoUrlMobile: PropTypes.string,
        planSizes: PropTypes.shape({
            '1-2': PropTypes.shape({
                maxBoxRange: PropTypes.number,
                minBoxRange: PropTypes.number,
                numItems: PropTypes.string,
            }),
            '3+': PropTypes.shape({
                maxBoxRange: PropTypes.number,
                minBoxRange: PropTypes.number,
                numItems: PropTypes.string,
            }),
        }),
        subPlans: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number,
            })
        ),
    }).isRequired,
    savedPlan: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        subPlanIDs: PropTypes.arrayOf(PropTypes.number),
    }),
    savedPlanSize: PropTypes.string,
};

PlanCard.defaultProps = {
    bundles: false,
    flexible: false,
    imperfectLegacy: false,
    savedPlan: null,
    savedPlanSize: '1-2',
};

export default PlanCard;
